/* eslint-disable import/no-named-as-default-member */
import { once } from "lodash";
import ReactGA from "react-ga4";
import { BaseItem, Item, SummaryItem } from "../models/item";
import { getMetadata, getProductIds, getProductInfo } from "../store/sagas/selectors";
import store from "../store/store";
import { getPublicArticleNumber } from "./itemUtilities";
import { DisplayPrice } from "../api/generated";
import Constants from "../constants";
import { makeUrlLowerCase } from "./navigationUtils";

export enum GaEcommerceAction {
    AddToCart,
    Purchase,
}

interface GaProduct {
    item_id?: string;
    item_name?: string;
    affiliation?: string;
    coupon?: string;
    discount?: number;
    index?: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    price?: number;
    quantity?: number;
}

function areCookiesEnabled() {
    try {
        document.cookie = "testcookie=test; Max-Age=10; SameSite=Strict;";
        const isEnabled = document.cookie.indexOf("testcookie") !== -1;
        document.cookie = "testcookie=; Max-Age=-1; SameSite=Strict;";
        return isEnabled;
    } catch {
        return false;
    }
}

function generateUniqueId() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
        /* eslint-disable no-bitwise */
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        /* eslint-enable no-bitwise */

        return v.toString(16);
    });
}

function setExtendedProductInfo(item: BaseItem, productId: number | undefined, product: GaProduct): GaProduct {
    const state = store.getState();
    const productInfo = getProductInfo(state);
    const metadata = getMetadata(state);
    const info = productInfo.get(productId);
    const metadataEntry = (metadata && metadata[item.id]) || {};
    const dynamicType = info?.dynamicType || metadataEntry?.DynamicType || Constants.genericFilterKey;
    const newProduct: GaProduct = {
        item_category: dynamicType,
        ...product,
    };

    Object.entries(metadataEntry)
        .concat(Object.entries(info?.filters || {}))
        .forEach(([key, value]) => {
            // Not sure if these are good keys to look for (copied from SDS 6),
            // they problably only exist some of the time.
            if (/CATEGORY/i.test(key)) {
                newProduct.item_category2 = value;
            } else if (/BRAND/i.test(key)) {
                newProduct.item_brand = value;
            } else if (/SERIES/i.test(key) || /COLLECTION/i.test(key)) {
                newProduct.item_category3 = value;
            }
        });

    return newProduct;
}

function getCurrentCleanedUrl(): URL {
    const url = new URL(window.location.href);
    url.searchParams.delete("config_string");
    url.searchParams.delete("encoded_config");
    url.hash = "";
    makeUrlLowerCase(url);
    return url;
}

// The custom dimensions are not set globally in GA4, so any properties
// that are set here will be manually added to all events.
const CustomDimensions: any = {};

const GaReporter = {
    enabled: false,
    transactionId: generateUniqueId(),

    initTracking(trackingIds: string[], useCookies: boolean, cookiePath: string, appType: string, appVersion: string) {
        if (!areCookiesEnabled()) {
            useCookies = false;
        }

        if (!useCookies) {
            // TODO: Decide if we should show consent banner. Setting analytics_storage to denied disables cookies,
            // but also makes no data show up at all in GA, so now we set temporary session cookies even without consent.
            // ReactGA.gtag("consent", "default", { ad_storage: "denied", analytics_storage: "denied" });
        }

        GaReporter.setCustomDimension("tracking_type", useCookies ? "cookie" : "none");
        GaReporter.setCustomDimension("application_type", appType);
        GaReporter.setCustomDimension("application_version", appVersion);

        const url = getCurrentCleanedUrl();
        const options: any[] = trackingIds.map(trackingId => ({
            trackingId: trackingId,
            gtagOptions: useCookies
                ? {
                      cookie_domain: "auto",
                      cookie_path: cookiePath,
                      cookie_flags: "secure;samesite=none",
                      send_page_view: false,
                      page_location: url.href,
                  }
                : {
                      // TODO: Disable cookies entirely while still collecting statistics.
                      cookie_domain: "auto",
                      cookie_path: cookiePath,
                      cookie_flags: "secure;samesite=none",
                      cookie_expires: 1, // Store a temporary cookie that is removed after 1 sec.
                      send_page_view: false,
                      page_location: url.href,
                  },
        }));

        ReactGA.initialize(options);
        GaReporter.enabled = true;
        GaReporter.setReferrerUrl();
    },

    setReferrerUrl() {
        const searchParams = new URLSearchParams(window.location.search);
        const siteUrl = searchParams.get("site_url");
        // Prefer setting site_url as referrer when going back and
        // forth between model_selector and configurator.
        if (siteUrl) {
            const url = new URL(siteUrl);
            makeUrlLowerCase(url);
            ReactGA.gtag("set", "page_referrer", url.href);
        } else if (document.referrer) {
            const referrer = new URL(document.referrer);
            makeUrlLowerCase(referrer);
            ReactGA.gtag("set", "page_referrer", referrer.href);
        }
    },

    setCustomDimension(key: string, value: string) {
        CustomDimensions[key] = value;
    },

    updateTransactionId() {
        GaReporter.transactionId = generateUniqueId();
    },

    setProject(project: string) {
        GaReporter.setCustomDimension("application_name", project);
    },

    setModelId(modelId: string) {
        GaReporter.setCustomDimension("model", modelId);
    },

    setAccountId(accountId: string) {
        GaReporter.setCustomDimension("account_id", accountId);
    },

    event(action: string, category?: string, label?: string, fields?: any, callback?: () => void) {
        if (!GaReporter.enabled) {
            if (callback) callback();
            return;
        }
        ReactGA.event(action, {
            event_category: category,
            event_label: label,
            event_callback: callback ? GaReporter.createHitCallback(callback) : undefined,
            ...fields,
            ...CustomDimensions,
        });
    },

    trackPageView() {
        if (!GaReporter.enabled) return;
        const url = getCurrentCleanedUrl();
        ReactGA.gtag("set", "page_location", url.href);
        GaReporter.event("page_view", undefined, undefined, { page_path: url.pathname });
    },

    trackItemActivation(item: Item, listName: string, trackPrice: boolean) {
        if (!GaReporter.enabled) return;
        let prod: GaProduct = {
            item_id: getPublicArticleNumber(item.articleNumber),
            item_name: item.shortText?.replace(/\n/g, " "),
            price: (trackPrice && item.price?.priceValue) || undefined,
        };

        let { productId } = item;
        if (!productId) {
            const state = store.getState();
            const productIds = getProductIds(state);
            productId = productIds[item.id];
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        prod = setExtendedProductInfo(item, productId, prod);

        const options: any = {
            item_list_name: listName,
            items: [prod],
        };
        GaReporter.event("view_item", "E-commerce", undefined, options);
    },

    trackProductCheckout(
        action: GaEcommerceAction,
        label: string | null,
        items: SummaryItem[],
        totalPrice: DisplayPrice | null,
        callback?: () => void
    ) {
        if (!GaReporter.enabled) {
            if (callback) callback();
            return;
        }
        const productIds = getProductIds(store.getState());
        const convertItem = (item: SummaryItem) => {
            let prod: GaProduct = {
                item_id: getPublicArticleNumber(item.articleNumber),
                item_name: item.name || undefined,
            };
            return setExtendedProductInfo(item, productIds[item.id], prod);
        };

        const currency = totalPrice ? totalPrice.currencySymbol : null;
        const price = totalPrice ? totalPrice.priceValue : null;
        const options: any = {
            currency,
            price,
            items: items.map(convertItem),
        };

        let gaEvent;
        if (action === GaEcommerceAction.Purchase) {
            gaEvent = "purchase";
            options.transaction_id = GaReporter.transactionId;
        } else if (action === GaEcommerceAction.AddToCart) {
            gaEvent = "add_to_cart";
        } else {
            throw new Error("Invalid GaEcommerceAction");
        }

        GaReporter.event(gaEvent, "E-commerce", label || undefined, options, callback);
    },

    /**
     * Creates a function to use as hitCallback for Google Analytics,
     * with a timeout that triggers the callback anyway in case the
     * analytics call fails or if GaReporter isn't enabled.
     */
    createHitCallback(callback: () => void) {
        const fn = once(callback);
        setTimeout(fn, GaReporter.enabled ? 1000 : 0);
        return fn;
    },
};

export default GaReporter;
